import { useMemo } from 'react';
import { Divider } from '@heroui/react';
import {
  ASSIGNMENT_SUBMISSION_STATUS_LABELS,
  AssignmentSubmissionStatusEnum,
} from '@/entities/Assignment';

interface TeacherAssignmentStatsProps {
  completed: number;
  total: number;
  averageScore?: number;
  submissionStatus?: AssignmentSubmissionStatusEnum;
}

export const TeacherAssignmentStats = ({
  completed,
  total,
  averageScore,
  submissionStatus,
}: TeacherAssignmentStatsProps) => {
  const gradeColor = useMemo(
    () =>
      typeof averageScore === 'number'
        ? averageScore < 50
          ? 'text-red-tertiary'
          : averageScore >= 50 && averageScore < 70
            ? 'text-yellow-tertiary'
            : 'text-green-secondary'
        : '',
    [averageScore],
  );

  const submissionStatusColor = useMemo(() => {
    if (!submissionStatus) return 'text-black';
    switch (submissionStatus) {
      case AssignmentSubmissionStatusEnum.Completed:
        return 'text-green-secondary';
      case AssignmentSubmissionStatusEnum.Failed:
        return 'text-red-warning';
      case AssignmentSubmissionStatusEnum.AwaitingReview:
        return 'text-yellow-tertiary';
      default:
        return 'text-black';
    }
  }, [submissionStatus]);

  const submissionStatusLabel = submissionStatus
    ? ASSIGNMENT_SUBMISSION_STATUS_LABELS[submissionStatus]
    : null;

  return (
    <div className="flex gap-6 items-center">
      <div className="flex gap-x-4 items-center">
        <p className="text-grey-secondary text-sm font-medium max-w-[115px]">
          The task has been completed
        </p>
        <p className="text-[24px] text-[#211D55] font-medium">
          {completed} / {total}
        </p>
      </div>
      {typeof averageScore === 'number' && (
        <>
          <Divider
            orientation="vertical"
            as="div"
            className="w-[1px] h-[44px] self-stretch bg-[#B3B3B3]"
          />
          <div className="flex gap-x-4 items-center">
            <p className="text-grey-secondary text-sm font-medium max-w-[115px]">
              Average group score
            </p>
            <p className={`text-[24px] font-medium ${gradeColor}`}>{averageScore}</p>
          </div>
        </>
      )}
      {submissionStatusLabel && (
        <>
          <Divider
            orientation="vertical"
            as="div"
            className="w-[1px] h-[44px] self-stretch bg-[#B3B3B3]"
          />
          <div className="flex gap-x-4 items-center">
            <p className="text-grey-secondary text-sm font-medium max-w-[115px]">
              Submission status
            </p>
            <p className={`text-[20px] font-medium ${submissionStatusColor}`}>
              {submissionStatusLabel}
            </p>
          </div>
        </>
      )}
    </div>
  );
};
